import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crosshair, Navigation, PersonStanding, Info, X } from 'lucide-react';
import { useEntitySelectionStore } from '@/stores/entity-selection-store';
import { useStreetViewStore } from '@/stores/street-view-store';
import { useDetailModalStore } from '@/stores/detail-modal-store';
import { getMarker } from '@/map/markers/marker-state';
import type { MarkerData } from '@/map/markers/marker-state';
import { cn } from '@/lib/utils';

/** HUD card for the selected entity: details + follow/track toggle + clear. */
export function SelectionCard(): React.JSX.Element {
  const selectedUid = useEntitySelectionStore((s) => s.selectedUid);
  const data = selectedUid ? getMarker(selectedUid) : undefined;

  return (
    <AnimatePresence>
      {data && <Card key={data.uid} data={data} />}
    </AnimatePresence>
  );
}

/** The animated card body for one selected entity. */
function Card({ data }: { data: MarkerData }): React.JSX.Element {
  const followMode = useEntitySelectionStore((s) => s.followMode);
  const toggleFollow = useEntitySelectionStore((s) => s.toggleFollow);
  const clear = useEntitySelectionStore((s) => s.clear);
  const openStreetView = useStreetViewStore((s) => s.openAt);
  const openDetails = useDetailModalStore((s) => s.open);

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -8 }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
      className="absolute bottom-3 left-[64px] z-[1000] w-72 overflow-hidden rounded-lg border border-echo-border bg-echo-grey/92 text-xs text-foreground shadow-2xl ring-1 ring-black/20 backdrop-blur"
    >
      <div className="flex items-center gap-2 border-b border-echo-border/60 px-3 py-2">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: data.color }} />
        <span className="flex-1 truncate font-chakra text-sm font-semibold text-foreground">
          {data.callsign}
        </span>
        <button type="button" onClick={clear} aria-label="Clear selection" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>

      {data.imageUrl && <CameraFeed url={data.imageUrl} />}

      <div className="space-y-0.5 px-3 py-2">
        <DetailRow label="Type" value={data.cotType} />
        <DetailRow label="Position" value={formatCoords(data.position)} />
        {(data.details ?? []).map((detail) => (
          <DetailRow key={detail.label} label={detail.label} value={detail.value} />
        ))}
      </div>

      <div className="flex border-t border-echo-border/60">
        <button
          type="button"
          onClick={toggleFollow}
          className={cn(
            'flex flex-1 items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider transition-colors',
            followMode ? 'bg-echo-yellow text-black' : 'text-muted-foreground hover:bg-echo-border/25',
          )}
        >
          {followMode ? <Navigation className="h-3.5 w-3.5 shrink-0" /> : <Crosshair className="h-3.5 w-3.5 shrink-0" />}
          {followMode ? 'Tracking' : 'Track'}
        </button>
        <button
          type="button"
          onClick={() => openDetails(data.uid)}
          title="Full details"
          className="flex flex-1 items-center justify-center gap-1.5 border-l border-echo-border/60 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:bg-echo-border/25"
        >
          <Info className="h-3.5 w-3.5 shrink-0" />
          Details
        </button>
        <button
          type="button"
          onClick={() => openStreetView({ lat: data.position.lat, lon: data.position.lng })}
          title="Street View at this location"
          className="flex flex-1 items-center justify-center gap-1.5 border-l border-echo-border/60 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:bg-echo-border/25"
        >
          <PersonStanding className="h-3.5 w-3.5 shrink-0" />
          Street
        </button>
      </div>
    </motion.div>
  );
}

/** Live-refreshing camera snapshot (cache-busted every few seconds). */
function CameraFeed({ url }: { url: string }): React.JSX.Element {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((value) => value + 1), 4000);
    return () => clearInterval(id);
  }, []);
  const src = `${url}${url.includes('?') ? '&' : '?'}t=${tick}`;
  return (
    <img
      src={src}
      alt="Camera feed"
      className="block w-full bg-black object-cover"
      style={{ aspectRatio: '16 / 9' }}
    />
  );
}

/** A label/value row. */
function DetailRow({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className="truncate text-right text-[11px] text-foreground">{value}</span>
    </div>
  );
}

/** Format coordinates to 4 decimals. */
function formatCoords(pos: google.maps.LatLngLiteral): string {
  return `${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)}`;
}
